import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import { Users, DollarSign, Key, Crown, TrendingUp, Receipt, CreditCard, CheckCircle, XCircle, Trash2, Edit2, Mail, Send, AlertTriangle, Download, Upload, Shield, Unlock, Cloud, HardDrive, RefreshCw, MessageSquare, Megaphone } from 'lucide-react';
import { toast } from 'sonner';

const Admin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [prices, setPrices] = useState({ monthly_price: 9.99, yearly_price: 99.99, trial_days: 7 });
  const [apiKeys, setApiKeys] = useState([]);
  const [emailSettings, setEmailSettings] = useState({
    smtp_host: '', smtp_port: 587, smtp_username: '', smtp_password: '',
    from_email: '', reminder_days_before: 7, reminder_enabled: false
  });
  const [newApiKey, setNewApiKey] = useState({ provider: 'openai', key_value: '' });
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [securityLogs, setSecurityLogs] = useState([]);
  const [blockedIPs, setBlockedIPs] = useState([]);
  const [s3Settings, setS3Settings] = useState({ aws_access_key_id: '', aws_secret_access_key: '', bucket_name: '', region: 'us-east-2', enabled: false });
  const [s3Status, setS3Status] = useState(null);
  const [testingS3, setTestingS3] = useState(false);
  const [appUrl, setAppUrl] = useState('');
  const [tokenHours, setTokenHours] = useState(24);
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(true);
  const [googleAuthEnabled, setGoogleAuthEnabled] = useState(true);
  const [supportSettings, setSupportSettings] = useState({ support_email: '', auto_reply_enabled: true, auto_reply_message: '' });
  const [supportMessages, setSupportMessages] = useState([]);
  const [broadcastSubject, setBroadcastSubject] = useState('');
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcastAudience, setBroadcastAudience] = useState('all');
  const [audienceCount, setAudienceCount] = useState(0);
  const [broadcastHistory, setBroadcastHistory] = useState([]);
  const [broadcastTemplates, setBroadcastTemplates] = useState([]);
  const [sendingBroadcast, setSendingBroadcast] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isModerator = user?.role === 'moderator';

  useEffect(() => {
    if (!isAdmin && !isModerator) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      // Fetch data based on role
      const commonPromises = [
        api.get('/admin/users'),
        api.get('/admin/payments'),
      ];
      
      // Admin-only data
      const adminPromises = isAdmin ? [
        api.get('/admin/stats'),
        api.get('/admin/subscribers'),
        api.get('/subscription/prices'),
        api.get('/admin/api-keys'),
        api.get('/admin/email-settings').catch(() => ({ data: emailSettings })),
        api.get('/admin/s3-settings').catch(() => ({ data: { enabled: false } })),
        api.get('/admin/app-settings').catch(() => ({ data: { app_url: '', verification_token_hours: 24, email_verification_required: true, google_auth_enabled: true } })),
        api.get('/admin/support-settings').catch(() => ({ data: { support_email: '', auto_reply_enabled: true, auto_reply_message: '' } })),
        api.get('/admin/support-messages').catch(() => ({ data: [] })),
        api.get('/admin/broadcast/history').catch(() => ({ data: [] })),
        api.get('/admin/broadcast/templates').catch(() => ({ data: [] })),
      ] : [];
      
      const results = await Promise.all([...commonPromises, ...adminPromises]);
      
      // Common data
      setUsers(results[0].data);
      setPayments(results[1].data);
      
      // Admin-only data
      if (isAdmin) {
        setStats(results[2].data);
        setSubscribers(results[3].data);
        setPrices(results[4].data);
        setApiKeys(results[5].data);
        setEmailSettings(results[6].data);
        if (results[7].data.enabled) {
          setS3Status(results[7].data);
          setS3Settings(prev => ({ ...prev, bucket_name: results[7].data.bucket_name, region: results[7].data.region, enabled: true }));
        }
        // Load app settings
        setAppUrl(results[8].data.app_url || '');
        setTokenHours(results[8].data.verification_token_hours || 24);
        setEmailVerificationRequired(results[8].data.email_verification_required !== false);
        setGoogleAuthEnabled(results[8].data.google_auth_enabled !== false);
        // Load support settings
        setSupportSettings(results[9].data);
        setSupportMessages(results[10].data);
        // Load broadcast data
        setBroadcastHistory(results[11].data);
        setBroadcastTemplates(results[12].data);
      }
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  // S3 Storage handlers
  const handleSaveS3Settings = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/admin/s3-settings', s3Settings);
      setS3Status(res.data);
      toast.success('S3 storage settings saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save S3 settings');
    }
  };

  const handleTestS3 = async () => {
    setTestingS3(true);
    try {
      const res = await api.post('/admin/s3-test');
      toast.success(`S3 connection successful! ${res.data.stats.total_objects} images stored.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'S3 connection failed');
    } finally {
      setTestingS3(false);
    }
  };

  const handleDeleteS3Settings = async () => {
    if (!window.confirm('Are you sure you want to disable cloud storage?')) return;
    try {
      await api.delete('/admin/s3-settings');
      setS3Status(null);
      setS3Settings({ aws_access_key_id: '', aws_secret_access_key: '', bucket_name: '', region: 'us-east-2', enabled: false });
      toast.success('S3 settings deleted');
    } catch (error) {
      toast.error('Failed to delete S3 settings');
    }
  };

  // User actions
  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? This will remove all their data permanently.`)) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleUpdateSubscription = async (userId) => {
    if (!editingUser) return;
    try {
      await api.put(`/admin/users/${userId}/subscription`, {
        subscription_status: editingUser.subscription_status,
        subscription_end_date: editingUser.subscription_end_date || null,
      });
      toast.success('Subscription updated');
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  const handleGrantPro = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/grant-pro`);
      toast.success('Pro access granted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to grant Pro access');
    }
  };

  const handleRevokePro = async (userId) => {
    if (!window.confirm('Revoke Pro access?')) return;
    try {
      await api.post(`/admin/users/${userId}/revoke-pro`);
      toast.success('Pro access revoked');
      fetchData();
    } catch (error) {
      toast.error('Failed to revoke Pro access');
    }
  };

  const handleAdjustDays = async (userId, days) => {
    try {
      const res = await api.post(`/admin/users/${userId}/adjust-days`, { days });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to adjust subscription days');
    }
  };

  const handleSendReminders = async () => {
    try {
      const res = await api.post('/admin/send-expiration-reminders');
      toast.success(`Sent ${res.data.sent} reminder emails (${res.data.skipped} skipped)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminders');
    }
  };

  // Payment actions
  const handleUpdatePaymentStatus = async (paymentId) => {
    if (!editingPayment) return;
    try {
      await api.put(`/admin/payments/${paymentId}/status`, {
        payment_status: editingPayment.payment_status,
      });
      toast.success('Payment status updated');
      setEditingPayment(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update payment');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment record?')) return;
    try {
      await api.delete(`/admin/payments/${paymentId}`);
      toast.success('Payment deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };

  // Settings actions
  const handleUpdatePrices = async (e) => {
    e.preventDefault();
    try {
      await api.put('/admin/prices', prices);
      toast.success('Prices updated!');
    } catch (error) {
      toast.error('Failed to update prices');
    }
  };

  const handleSaveApiKey = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/api-keys', newApiKey);
      toast.success('API key saved!');
      setNewApiKey({ provider: 'openai', key_value: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save API key');
    }
  };

  const handleSaveEmailSettings = async (e) => {
    e.preventDefault();
    try {
      await api.put('/admin/email-settings', emailSettings);
      toast.success('Email settings saved!');
    } catch (error) {
      toast.error('Failed to save email settings');
    }
  };

  const handleTestEmail = async () => {
    try {
      const res = await api.post('/admin/test-email');
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  const formatDate = (date) => date ? new Date(date).toLocaleDateString('en-US') : '-';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712]">
      <Navbar />
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <Crown className="w-10 h-10 text-yellow-400" />
            <h1 className="text-4xl font-bold font-['Outfit']">Admin Panel</h1>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 p-1 bg-white/5 rounded-xl">
          {(isAdmin 
            ? ['stats', 'users', 'payments', 'prices', 'api-keys', 'email', 'storage', 'security', 'config', 'broadcast', 'support']
            : ['users', 'payments']
          ).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              data-testid={`tab-${tab}`}
              className={`px-4 py-2 rounded-lg font-medium transition-all capitalize ${
                activeTab === tab ? 'bg-white text-black' : 'text-muted-foreground hover:text-white'
              }`}
            >
              {tab === 'api-keys' ? 'API Keys' : tab}
            </button>
          ))}
        </div>

        {/* Stats Tab */}
        {activeTab === 'stats' && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-6 bg-gradient-to-br from-green-500/20 to-transparent border border-green-500/30 rounded-2xl">
              <DollarSign className="w-8 h-8 text-green-400 mb-2" />
              <p className="text-sm text-green-400">Total Revenue</p>
              <p className="text-2xl font-bold text-green-400">{formatCurrency(stats.total_revenue)}</p>
            </div>
            <div className="p-6 bg-gradient-to-br from-indigo-500/20 to-transparent border border-indigo-500/30 rounded-2xl">
              <TrendingUp className="w-8 h-8 text-indigo-400 mb-2" />
              <p className="text-sm text-indigo-400">Monthly Revenue</p>
              <p className="text-2xl font-bold text-indigo-400">{formatCurrency(stats.monthly_revenue)}</p>
            </div>
            <div className="p-6 bg-gradient-to-br from-yellow-500/20 to-transparent border border-yellow-500/30 rounded-2xl">
              <Crown className="w-8 h-8 text-yellow-400 mb-2" />
              <p className="text-sm text-yellow-400">Pro Users</p>
              <p className="text-2xl font-bold text-yellow-400">{stats.active_subscribers}</p>
            </div>
            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
              <Users className="w-8 h-8 text-white mb-2" />
              <p className="text-sm text-muted-foreground">Total Users</p>
              <p className="text-2xl font-bold">{stats.total_users}</p>
            </div>
            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
              <Receipt className="w-8 h-8 text-cyan-400 mb-2" />
              <p className="text-sm text-muted-foreground">Receipts This Month</p>
              <p className="text-2xl font-bold">{stats.receipts_this_month}</p>
            </div>
            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
              <Receipt className="w-8 h-8 text-purple-400 mb-2" />
              <p className="text-sm text-muted-foreground">Total Receipts</p>
              <p className="text-2xl font-bold">{stats.total_receipts}</p>
            </div>
            
            {/* Subscription Reminder Control */}
            <div className="col-span-2 md:col-span-4 p-6 bg-gradient-to-br from-orange-500/10 to-transparent border border-orange-500/30 rounded-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-orange-400 flex items-center">
                    <Mail className="w-5 h-5 mr-2" />
                    Subscription Reminders
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Automatic emails are sent daily at 9:00 AM UTC for expiring subscriptions
                  </p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      await api.post('/admin/run-subscription-check');
                      toast.success('Subscription check completed! Check server logs.');
                    } catch (e) { toast.error('Failed to run check'); }
                  }}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-full text-sm font-medium"
                >
                  Run Check Now
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            {users.map((u) => (
              <div key={u.id} className="p-4 bg-white/5 border border-white/10 rounded-xl">
                {editingUser?.id === u.id ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{u.name}</p>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-muted-foreground mb-1">Status</label>
                        <select
                          value={editingUser.subscription_status}
                          onChange={(e) => setEditingUser({ ...editingUser, subscription_status: e.target.value })}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                        >
                          <option value="free">Free</option>
                          <option value="active">Active (Pro)</option>
                          <option value="expired">Expired</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-muted-foreground mb-1">End Date</label>
                        <input
                          type="date"
                          value={editingUser.subscription_end_date?.split('T')[0] || ''}
                          onChange={(e) => setEditingUser({ ...editingUser, subscription_end_date: e.target.value })}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                        />
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button onClick={() => handleUpdateSubscription(u.id)} className="px-4 py-2 bg-green-500 text-white rounded-lg">Save</button>
                      <button onClick={() => setEditingUser(null)} className="px-4 py-2 bg-white/10 rounded-lg">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{u.name}</p>
                      <p className="text-sm text-muted-foreground">{u.email}</p>
                      {/* Show password hash for admin */}
                      {isAdmin && u.password && (
                        <p className="text-xs text-orange-400 mt-1 font-mono truncate max-w-xs" title={u.password}>
                          Password: {u.password.substring(0, 20)}...
                        </p>
                      )}
                      {isAdmin && !u.password && (
                        <p className="text-xs text-gray-500 mt-1">No password (OAuth user)</p>
                      )}
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          u.subscription_status === 'active' ? 'bg-green-500/20 text-green-400' :
                          u.subscription_status === 'expired' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {u.subscription_status}
                        </span>
                        {u.role === 'admin' && <span className="px-2 py-0.5 rounded text-xs bg-yellow-500/20 text-yellow-400">Admin</span>}
                        {u.role === 'moderator' && <span className="px-2 py-0.5 rounded text-xs bg-blue-500/20 text-blue-400">Moderator</span>}
                        {u.subscription_status === 'active' && u.subscription_end_date && (
                          <span className="px-2 py-0.5 rounded text-xs bg-blue-500/20 text-blue-400">
                            {Math.max(0, Math.ceil((new Date(u.subscription_end_date) - new Date()) / (1000*60*60*24)))} days left
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {/* Role selector - admin only */}
                      {isAdmin && u.id !== user.id && (
                        <select
                          value={u.role}
                          onChange={async (e) => {
                            try {
                              await api.put(`/admin/users/${u.id}/role`, null, { params: { role: e.target.value } });
                              toast.success('Role updated');
                              fetchData();
                            } catch (err) { toast.error('Failed to update role'); }
                          }}
                          className="text-xs px-2 py-1 bg-white/10 border border-white/20 rounded-lg text-white"
                        >
                          <option value="user">User</option>
                          <option value="moderator">Moderator</option>
                          <option value="admin">Admin</option>
                        </select>
                      )}
                      {u.subscription_status === 'active' && u.role !== 'admin' && (
                        <>
                          <div className="flex items-center space-x-1 mr-2">
                            <button 
                              onClick={() => handleAdjustDays(u.id, -7)} 
                              data-testid={`minus-7-${u.id}`}
                              className="px-2 py-1 text-xs text-red-400 hover:bg-red-400/10 rounded border border-red-400/30"
                            >
                              -7d
                            </button>
                            <button 
                              onClick={() => handleAdjustDays(u.id, -30)} 
                              data-testid={`minus-30-${u.id}`}
                              className="px-2 py-1 text-xs text-red-400 hover:bg-red-400/10 rounded border border-red-400/30"
                            >
                              -30d
                            </button>
                            <button 
                              onClick={() => handleAdjustDays(u.id, 7)} 
                              data-testid={`plus-7-${u.id}`}
                              className="px-2 py-1 text-xs text-green-400 hover:bg-green-400/10 rounded border border-green-400/30"
                            >
                              +7d
                            </button>
                            <button 
                              onClick={() => handleAdjustDays(u.id, 30)} 
                              data-testid={`plus-30-${u.id}`}
                              className="px-2 py-1 text-xs text-green-400 hover:bg-green-400/10 rounded border border-green-400/30"
                            >
                              +30d
                            </button>
                          </div>
                          <button onClick={() => handleRevokePro(u.id)} className="px-3 py-1 text-sm text-red-400 hover:bg-red-400/10 rounded">Revoke</button>
                        </>
                      )}
                      {u.subscription_status !== 'active' && u.role !== 'admin' && (
                        <button onClick={() => handleGrantPro(u.id)} className="px-3 py-1 text-sm text-green-400 hover:bg-green-400/10 rounded">Grant Pro</button>
                      )}
                      {/* Reset Password Button */}
                      {isAdmin && u.password && u.role !== 'admin' && (
                        <button 
                          onClick={async () => {
                            if (!confirm(`Reset password for ${u.email}? New password will be sent to their email.`)) return;
                            try {
                              const res = await api.post(`/admin/users/${u.id}/reset-password`);
                              toast.success(`Password reset! New password: ${res.data.new_password}`);
                              alert(`New password for ${u.email}:\n\n${res.data.new_password}\n\nThis has been sent to their email.`);
                            } catch (err) { 
                              toast.error(err.response?.data?.detail || 'Failed to reset password'); 
                            }
                          }} 
                          className="px-3 py-1 text-sm text-orange-400 hover:bg-orange-400/10 rounded flex items-center space-x-1"
                          title="Reset password"
                        >
                          <Unlock className="w-3 h-3" />
                          <span>Reset</span>
                        </button>
                      )}
                      <button onClick={() => setEditingUser({ ...u })} className="p-2 hover:bg-white/10 rounded"><Edit2 className="w-4 h-4" /></button>
                      {u.role !== 'admin' && (
                        <button onClick={() => handleDeleteUser(u.id, u.email)} className="p-2 hover:bg-red-400/10 rounded text-red-400"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="space-y-4">
            {payments.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground bg-white/5 rounded-xl">
                <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No payments yet</p>
              </div>
            ) : (
              payments.map((p) => (
                <div key={p.id} className="p-4 bg-white/5 border border-white/10 rounded-xl">
                  {editingPayment?.id === p.id ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{p.user_name}</p>
                          <p className="text-sm text-muted-foreground">{formatCurrency(p.amount)}</p>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-muted-foreground mb-1">Payment Status</label>
                        <select
                          value={editingPayment.payment_status}
                          onChange={(e) => setEditingPayment({ ...editingPayment, payment_status: e.target.value })}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                        >
                          <option value="pending">Pending</option>
                          <option value="paid">Paid</option>
                          <option value="failed">Failed</option>
                        </select>
                      </div>
                      <div className="flex space-x-2">
                        <button onClick={() => handleUpdatePaymentStatus(p.id)} className="px-4 py-2 bg-green-500 text-white rounded-lg">Save</button>
                        <button onClick={() => setEditingPayment(null)} className="px-4 py-2 bg-white/10 rounded-lg">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{p.user_name || p.user_email}</p>
                        <p className="text-sm text-muted-foreground">{p.user_email}</p>
                        <p className="text-xs text-muted-foreground mt-1">{formatDate(p.created_at)}</p>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <p className="font-mono text-green-400">{formatCurrency(p.amount)}</p>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            p.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' :
                            p.payment_status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {p.payment_status}
                          </span>
                        </div>
                        <div className="flex space-x-1">
                          <button onClick={() => setEditingPayment({ ...p })} className="p-2 hover:bg-white/10 rounded"><Edit2 className="w-4 h-4" /></button>
                          <button onClick={() => handleDeletePayment(p.id)} className="p-2 hover:bg-red-400/10 rounded text-red-400"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Prices Tab */}
        {activeTab === 'prices' && (
          <div className="p-6 bg-white/5 border border-white/10 rounded-2xl max-w-md">
            <h2 className="text-xl font-semibold mb-4">Subscription Prices</h2>
            <form onSubmit={handleUpdatePrices} className="space-y-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Monthly Price (USD)</label>
                <input type="number" step="0.01" value={prices.monthly_price} onChange={(e) => setPrices({ ...prices, monthly_price: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white" />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Yearly Price (USD)</label>
                <input type="number" step="0.01" value={prices.yearly_price} onChange={(e) => setPrices({ ...prices, yearly_price: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white" />
              </div>
              <button type="submit" className="bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-2 rounded-full">Save Prices</button>
            </form>
          </div>
        )}

        {/* API Keys Tab */}
        {activeTab === 'api-keys' && (
          <div className="space-y-6">
            {apiKeys.length > 0 && (
              <div className="space-y-3">
                <h2 className="text-xl font-semibold">Current Keys</h2>
                {apiKeys.map((key) => (
                  <div key={key.id} className="p-4 bg-white/5 border border-white/10 rounded-xl flex justify-between">
                    <div>
                      <p className="font-medium capitalize">{key.provider}</p>
                      <p className="text-sm text-muted-foreground font-mono">{key.key_value?.substring(0, 20)}...</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl max-w-md">
              <h2 className="text-xl font-semibold mb-4">Add API Key</h2>
              <form onSubmit={handleSaveApiKey} className="space-y-4">
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Provider</label>
                  <select value={newApiKey.provider} onChange={(e) => setNewApiKey({ ...newApiKey, provider: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white">
                    <option value="openai">OpenAI (OCR)</option>
                    <option value="stripe">Stripe (Payments)</option>
                    <option value="sendgrid">SendGrid (Email)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">API Key</label>
                  <input type="text" value={newApiKey.key_value} onChange={(e) => setNewApiKey({ ...newApiKey, key_value: e.target.value })}
                    placeholder="sk-..." className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white" />
                </div>
                <button type="submit" className="bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-2 rounded-full">Save Key</button>
              </form>
            </div>
          </div>
        )}

        {/* Email Tab */}
        {activeTab === 'email' && (
          <div className="p-6 bg-white/5 border border-white/10 rounded-2xl max-w-lg">
            <div className="flex items-center space-x-2 mb-4">
              <Mail className="w-6 h-6 text-indigo-400" />
              <h2 className="text-xl font-semibold">Email Settings</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-6">Configure SMTP for subscription reminders</p>
            <form onSubmit={handleSaveEmailSettings} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">SMTP Host</label>
                  <input type="text" value={emailSettings.smtp_host} onChange={(e) => setEmailSettings({ ...emailSettings, smtp_host: e.target.value })}
                    placeholder="smtp.gmail.com" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white" />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Port</label>
                  <input type="number" value={emailSettings.smtp_port} onChange={(e) => setEmailSettings({ ...emailSettings, smtp_port: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white" />
                </div>
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Username</label>
                <input type="text" value={emailSettings.smtp_username} onChange={(e) => setEmailSettings({ ...emailSettings, smtp_username: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white" />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Password</label>
                <input type="password" value={emailSettings.smtp_password} onChange={(e) => setEmailSettings({ ...emailSettings, smtp_password: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white" />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">From Email</label>
                <input type="email" value={emailSettings.from_email} onChange={(e) => setEmailSettings({ ...emailSettings, from_email: e.target.value })}
                  placeholder="noreply@finscanusa.com" className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Remind Days Before</label>
                  <input type="number" value={emailSettings.reminder_days_before} onChange={(e) => setEmailSettings({ ...emailSettings, reminder_days_before: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white" />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input type="checkbox" checked={emailSettings.reminder_enabled} onChange={(e) => setEmailSettings({ ...emailSettings, reminder_enabled: e.target.checked })}
                      className="w-4 h-4 rounded" />
                    <span className="text-sm">Enable Reminders</span>
                  </label>
                </div>
              </div>
              <div className="flex space-x-3 pt-2">
                <button type="submit" className="bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-2 rounded-full">Save Settings</button>
                <button type="button" onClick={handleTestEmail} className="bg-white/10 hover:bg-white/20 px-6 py-2 rounded-full flex items-center space-x-2">
                  <Send className="w-4 h-4" /><span>Test Email</span>
                </button>
              </div>
            </form>
            {/* Send Reminders Button */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">Send Expiration Reminders</h3>
                  <p className="text-sm text-muted-foreground">Send emails to users with expiring subscriptions</p>
                </div>
                <button 
                  onClick={handleSendReminders}
                  data-testid="send-reminders-btn"
                  disabled={!emailSettings.reminder_enabled}
                  className={`px-4 py-2 rounded-full flex items-center space-x-2 ${
                    emailSettings.reminder_enabled 
                      ? 'bg-amber-500 hover:bg-amber-600 text-white' 
                      : 'bg-gray-500/20 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <AlertTriangle className="w-4 h-4" />
                  <span>Send Reminders Now</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Storage Tab - AWS S3 */}
        {activeTab === 'storage' && (
          <div className="space-y-6 max-w-2xl">
            {/* Current Status */}
            {s3Status && s3Status.enabled && (
              <div className="p-6 bg-gradient-to-br from-orange-500/20 to-transparent border border-orange-500/30 rounded-2xl">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <Cloud className="w-6 h-6 text-orange-400" />
                    <h2 className="text-xl font-semibold text-orange-400">AWS S3 Cloud Storage Active</h2>
                  </div>
                  <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">Connected</span>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Provider</p>
                    <p className="font-medium">AWS S3</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Bucket</p>
                    <p className="font-medium font-mono">{s3Status.bucket_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Region</p>
                    <p className="font-medium">{s3Status.region}</p>
                  </div>
                  {s3Status.storage_stats && (
                    <>
                      <div>
                        <p className="text-sm text-muted-foreground">Images Stored</p>
                        <p className="font-medium">{s3Status.storage_stats.total_objects || 0}</p>
                      </div>
                    </>
                  )}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleTestS3}
                    disabled={testingS3}
                    data-testid="test-s3-btn"
                    className="px-4 py-2 bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 rounded-lg flex items-center space-x-2"
                  >
                    <RefreshCw className={`w-4 h-4 ${testingS3 ? 'animate-spin' : ''}`} />
                    <span>{testingS3 ? 'Testing...' : 'Test Connection'}</span>
                  </button>
                  <button
                    onClick={handleDeleteS3Settings}
                    data-testid="delete-s3-btn"
                    className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg"
                  >
                    Disable
                  </button>
                </div>
              </div>
            )}

            {/* Configuration Form */}
            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
              <div className="flex items-center space-x-2 mb-4">
                <HardDrive className="w-6 h-6 text-orange-400" />
                <h2 className="text-xl font-semibold">{s3Status?.enabled ? 'Update' : 'Configure'} AWS S3</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                Cloud storage for premium users. AWS Free Tier: 5GB storage, 20,000 GET, 2,000 PUT requests/month.
              </p>
              <form onSubmit={handleSaveS3Settings} className="space-y-4">
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Access Key ID</label>
                  <input
                    type="text"
                    value={s3Settings.aws_access_key_id}
                    onChange={(e) => setS3Settings({ ...s3Settings, aws_access_key_id: e.target.value })}
                    placeholder="AKIA..."
                    data-testid="s3-access-key"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Secret Access Key</label>
                  <input
                    type="password"
                    value={s3Settings.aws_secret_access_key}
                    onChange={(e) => setS3Settings({ ...s3Settings, aws_secret_access_key: e.target.value })}
                    placeholder="Your AWS Secret Access Key"
                    data-testid="s3-secret-key"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Bucket Name</label>
                  <input
                    type="text"
                    value={s3Settings.bucket_name}
                    onChange={(e) => setS3Settings({ ...s3Settings, bucket_name: e.target.value })}
                    placeholder="finscan-receipts"
                    data-testid="s3-bucket"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Region</label>
                  <select
                    value={s3Settings.region}
                    onChange={(e) => setS3Settings({ ...s3Settings, region: e.target.value })}
                    data-testid="s3-region"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                  >
                    <option value="us-east-1">US East (N. Virginia)</option>
                    <option value="us-east-2">US East (Ohio)</option>
                    <option value="us-west-1">US West (N. California)</option>
                    <option value="us-west-2">US West (Oregon)</option>
                    <option value="eu-west-1">EU (Ireland)</option>
                    <option value="eu-central-1">EU (Frankfurt)</option>
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="s3-enabled"
                    checked={s3Settings.enabled}
                    onChange={(e) => setS3Settings({ ...s3Settings, enabled: e.target.checked })}
                    data-testid="s3-enabled"
                    className="w-4 h-4 rounded"
                  />
                  <label htmlFor="s3-enabled" className="text-sm">Enable cloud storage for Pro users</label>
                </div>
                <button
                  type="submit"
                  data-testid="save-s3-btn"
                  className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-full flex items-center space-x-2"
                >
                  <Cloud className="w-4 h-4" />
                  <span>Save S3 Settings</span>
                </button>
              </form>
            </div>

            {/* Info Box */}
            <div className="p-6 bg-blue-500/10 border border-blue-500/30 rounded-2xl">
              <h3 className="font-semibold text-blue-400 mb-3">How Cloud Storage Works</h3>
              <ul className="text-sm text-gray-400 space-y-2">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <span><strong>Free users:</strong> Images stored locally in browser (IndexedDB)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <span><strong>Pro users:</strong> Images automatically backed up to Cloudflare R2</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Images are accessible from any device for Pro subscribers</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Cloudflare R2 has no egress fees (data transfer is free)</span>
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="space-y-6">
            {/* Blocked IPs Section */}
            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Shield className="w-6 h-6 text-red-400" />
                  <h2 className="text-xl font-semibold">Blocked IP Addresses</h2>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const res = await api.get('/admin/blocked-ips');
                      setBlockedIPs(res.data);
                    } catch (error) {
                      toast.error('Failed to load blocked IPs');
                    }
                  }}
                  className="px-3 py-1 bg-white/10 rounded-lg text-sm hover:bg-white/20"
                >
                  Refresh
                </button>
              </div>
              
              {blockedIPs.length === 0 ? (
                <p className="text-muted-foreground text-sm">No blocked IP addresses</p>
              ) : (
                <div className="space-y-2">
                  {blockedIPs.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <div>
                        <p className="font-mono text-red-400">{item.ip}</p>
                        <p className="text-sm text-muted-foreground">
                          {item.attempts} failed attempts • Unblocks in {item.remaining_minutes} min
                        </p>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            await api.post(`/admin/unblock-ip/${item.ip}`);
                            toast.success(`IP ${item.ip} unblocked`);
                            const res = await api.get('/admin/blocked-ips');
                            setBlockedIPs(res.data);
                          } catch (error) {
                            toast.error('Failed to unblock IP');
                          }
                        }}
                        className="p-2 bg-white/10 rounded-lg hover:bg-white/20"
                      >
                        <Unlock className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Security Logs Section */}
            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-6 h-6 text-yellow-400" />
                  <h2 className="text-xl font-semibold">Security Logs</h2>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const res = await api.get('/admin/security-logs?limit=50');
                      setSecurityLogs(res.data);
                    } catch (error) {
                      toast.error('Failed to load security logs');
                    }
                  }}
                  className="px-3 py-1 bg-white/10 rounded-lg text-sm hover:bg-white/20"
                >
                  Load Logs
                </button>
              </div>

              {securityLogs.length === 0 ? (
                <p className="text-muted-foreground text-sm">Click "Load Logs" to view security events</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {securityLogs.map((log) => (
                    <div 
                      key={log.id} 
                      className={`p-3 rounded-lg border ${
                        log.event_type.includes('failed') || log.event_type.includes('blocked') 
                          ? 'bg-red-500/10 border-red-500/30' 
                          : 'bg-white/5 border-white/10'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-medium ${
                          log.event_type.includes('failed') || log.event_type.includes('blocked')
                            ? 'text-red-400'
                            : 'text-green-400'
                        }`}>
                          {log.event_type.replace(/_/g, ' ').toUpperCase()}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {log.details?.email && <span>Email: {log.details.email} </span>}
                        {log.details?.ip && <span>• IP: {log.details.ip}</span>}
                        {log.details?.attempts_left !== undefined && (
                          <span> • Attempts left: {log.details.attempts_left}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Security Info */}
            <div className="p-6 bg-green-500/10 border border-green-500/30 rounded-2xl">
              <h3 className="text-lg font-semibold text-green-400 mb-3">Security Features Active</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>Rate limiting: 5 failed attempts = 15 min block</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>Password requirements: 8+ chars, uppercase, lowercase, digit</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>Secure image access: Auth required for receipt images</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>Bcrypt password hashing</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>JWT token authentication with 7-day expiry</span>
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* Config Tab */}
        {activeTab === 'config' && (
          <div className="space-y-6 max-w-2xl">
            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
              <div className="flex items-center space-x-2 mb-4">
                <Download className="w-6 h-6 text-green-400" />
                <h2 className="text-xl font-semibold">Export Configuration</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Download all settings (API keys, email, prices) for server migration
              </p>
              <button
                onClick={async () => {
                  try {
                    const res = await api.get('/admin/export-config');
                    const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `finscan-config-${new Date().toISOString().split('T')[0]}.json`;
                    a.click();
                    toast.success('Config exported!');
                  } catch (e) {
                    toast.error('Export failed');
                  }
                }}
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-full flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Export Config</span>
              </button>
            </div>

            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl mb-6">
              <div className="flex items-center space-x-2 mb-4">
                <Key className="w-6 h-6 text-green-400" />
                <h2 className="text-xl font-semibold">App Settings</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">APP URL (for emails)</label>
                  <input
                    type="text"
                    value={appUrl}
                    onChange={(e) => setAppUrl(e.target.value)}
                    placeholder="https://finscan.store"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Verification Token Expiry</label>
                  <select
                    value={tokenHours}
                    onChange={(e) => setTokenHours(parseInt(e.target.value))}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                  >
                    <option value="2">2 hours</option>
                    <option value="12">12 hours</option>
                    <option value="24">24 hours</option>
                    <option value="48">48 hours</option>
                    <option value="72">72 hours</option>
                  </select>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="email-verification-required"
                    checked={emailVerificationRequired}
                    onChange={(e) => setEmailVerificationRequired(e.target.checked)}
                    className="w-4 h-4 rounded"
                  />
                  <label htmlFor="email-verification-required" className="text-sm">Require email verification</label>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="google-auth-enabled"
                    checked={googleAuthEnabled}
                    onChange={(e) => setGoogleAuthEnabled(e.target.checked)}
                    className="w-4 h-4 rounded"
                  />
                  <label htmlFor="google-auth-enabled" className="text-sm">Enable Google authentication</label>
                </div>
                <button
                  onClick={async () => {
                    try {
                      await api.post('/admin/app-settings', { 
                        app_url: appUrl, 
                        verification_token_hours: tokenHours,
                        email_verification_required: emailVerificationRequired,
                        google_auth_enabled: googleAuthEnabled
                      });
                      toast.success('Settings saved!');
                    } catch (e) { toast.error('Failed to save'); }
                  }}
                  className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-full"
                >
                  Save Settings
                </button>
              </div>
            </div>

            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
              <div className="flex items-center space-x-2 mb-4">
                <Upload className="w-6 h-6 text-indigo-400" />
                <h2 className="text-xl font-semibold">Import Configuration</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Upload config file from another server
              </p>
              <input
                type="file"
                accept=".json"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  try {
                    const text = await file.text();
                    const config = JSON.parse(text);
                    await api.post('/admin/import-config', config);
                    toast.success('Config imported!');
                    fetchData();
                  } catch (err) {
                    toast.error('Import failed - invalid file');
                  }
                }}
                className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-indigo-500 file:text-white hover:file:bg-indigo-600"
              />
            </div>

            <div className="p-6 bg-yellow-500/10 border border-yellow-500/30 rounded-2xl">
              <h3 className="font-semibold text-yellow-400 mb-2">Migration Instructions</h3>
              <ol className="text-sm text-gray-400 space-y-2 list-decimal list-inside">
                <li>Export config from this server</li>
                <li>Set up new server with MongoDB</li>
                <li>Update <code className="bg-white/10 px-1 rounded">backend/.env</code>: MONGO_URL, DB_NAME</li>
                <li>Update <code className="bg-white/10 px-1 rounded">frontend/.env</code>: REACT_APP_BACKEND_URL</li>
                <li>Import config on new server via Admin Panel</li>
              </ol>
            </div>
          </div>
        )}

        {/* Broadcast Tab */}
        {activeTab === 'broadcast' && isAdmin && (
          <div className="space-y-6">
            {/* Send Broadcast */}
            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
              <div className="flex items-center space-x-2 mb-4">
                <Megaphone className="w-6 h-6 text-orange-400" />
                <h2 className="text-xl font-semibold">Send Broadcast Email</h2>
              </div>
              
              <div className="space-y-4">
                {/* Audience Selection */}
                <div>
                  <label className="block text-sm text-muted-foreground mb-2">Select Audience</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { id: 'all', label: 'All Users', color: 'indigo' },
                      { id: 'free', label: 'Free Users', color: 'gray' },
                      { id: 'pro', label: 'Pro Users', color: 'yellow' },
                      { id: 'expired', label: 'Expired', color: 'red' },
                    ].map((aud) => (
                      <button
                        key={aud.id}
                        onClick={async () => {
                          setBroadcastAudience(aud.id);
                          try {
                            const res = await api.get(`/admin/broadcast/audience-count?audience=${aud.id}`);
                            setAudienceCount(res.data.count);
                          } catch (e) { setAudienceCount(0); }
                        }}
                        className={`p-3 rounded-xl border transition-all text-center ${
                          broadcastAudience === aud.id
                            ? `bg-${aud.color}-500/20 border-${aud.color}-500 text-white`
                            : 'bg-white/5 border-white/10 text-muted-foreground hover:border-white/20'
                        }`}
                      >
                        <span className="text-sm font-medium">{aud.label}</span>
                      </button>
                    ))}
                  </div>
                  {audienceCount > 0 && (
                    <p className="text-sm text-muted-foreground mt-2">
                      Will be sent to <span className="text-white font-semibold">{audienceCount}</span> users
                    </p>
                  )}
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Subject</label>
                  <input
                    type="text"
                    value={broadcastSubject}
                    onChange={(e) => setBroadcastSubject(e.target.value)}
                    placeholder="e.g., Special Offer: 50% Off Pro!"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Message</label>
                  <textarea
                    value={broadcastMessage}
                    onChange={(e) => setBroadcastMessage(e.target.value)}
                    placeholder="Write your message here..."
                    rows={6}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white resize-none"
                  />
                </div>

                {/* Templates */}
                {broadcastTemplates.length > 0 && (
                  <div>
                    <label className="block text-sm text-muted-foreground mb-2">Or use a template:</label>
                    <div className="flex flex-wrap gap-2">
                      {broadcastTemplates.map((tpl) => (
                        <button
                          key={tpl.id}
                          onClick={() => {
                            setBroadcastSubject(tpl.subject);
                            setBroadcastMessage(tpl.message);
                          }}
                          className="px-3 py-1 bg-white/10 hover:bg-white/20 rounded-full text-sm transition-colors"
                        >
                          {tpl.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center space-x-3 pt-4">
                  <button
                    onClick={async () => {
                      if (!broadcastSubject || !broadcastMessage) {
                        toast.error('Please fill in subject and message');
                        return;
                      }
                      if (audienceCount === 0) {
                        toast.error('No users in selected audience');
                        return;
                      }
                      if (!confirm(`Send email to ${audienceCount} users?`)) return;
                      
                      setSendingBroadcast(true);
                      try {
                        const res = await api.post('/admin/broadcast/send', {
                          subject: broadcastSubject,
                          message: broadcastMessage,
                          audience: broadcastAudience
                        });
                        toast.success(`Sent to ${res.data.sent_count} users!`);
                        setBroadcastSubject('');
                        setBroadcastMessage('');
                        fetchData();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Failed to send');
                      } finally {
                        setSendingBroadcast(false);
                      }
                    }}
                    disabled={sendingBroadcast || !broadcastSubject || !broadcastMessage}
                    className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-6 py-2 rounded-full font-medium disabled:opacity-50 flex items-center space-x-2"
                  >
                    {sendingBroadcast ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>Send Broadcast</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={async () => {
                      const name = prompt('Template name:');
                      if (!name || !broadcastSubject || !broadcastMessage) return;
                      try {
                        await api.post('/admin/broadcast/templates', {
                          name,
                          subject: broadcastSubject,
                          message: broadcastMessage
                        });
                        toast.success('Template saved!');
                        fetchData();
                      } catch (e) { toast.error('Failed to save template'); }
                    }}
                    disabled={!broadcastSubject || !broadcastMessage}
                    className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-full text-sm disabled:opacity-50"
                  >
                    Save as Template
                  </button>
                </div>
              </div>
            </div>

            {/* Broadcast History */}
            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Broadcast History</h2>
                <span className="text-sm text-muted-foreground">{broadcastHistory.length} broadcasts</span>
              </div>
              {broadcastHistory.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No broadcasts sent yet</p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {broadcastHistory.map((b) => (
                    <div key={b.id} className="p-4 bg-white/5 rounded-xl border border-white/10">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{b.subject}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          b.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {b.status}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">{b.message}</p>
                      <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                        <span>
                          Audience: <span className="capitalize">{b.audience}</span> • 
                          Sent: {b.sent_count}/{b.recipients_count}
                          {b.failed_count > 0 && <span className="text-red-400"> • Failed: {b.failed_count}</span>}
                        </span>
                        <span>{new Date(b.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Saved Templates */}
            {broadcastTemplates.length > 0 && (
              <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
                <h2 className="text-xl font-semibold mb-4">Saved Templates</h2>
                <div className="space-y-2">
                  {broadcastTemplates.map((tpl) => (
                    <div key={tpl.id} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                      <div>
                        <span className="font-medium">{tpl.name}</span>
                        <span className="text-sm text-muted-foreground ml-2">- {tpl.subject}</span>
                      </div>
                      <button
                        onClick={async () => {
                          if (!confirm('Delete this template?')) return;
                          try {
                            await api.delete(`/admin/broadcast/templates/${tpl.id}`);
                            fetchData();
                          } catch (e) { toast.error('Failed to delete'); }
                        }}
                        className="text-red-400 hover:text-red-300 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Support Tab */}
        {activeTab === 'support' && isAdmin && (
          <div className="space-y-6">
            {/* Support Settings */}
            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
              <div className="flex items-center space-x-2 mb-4">
                <MessageSquare className="w-6 h-6 text-indigo-400" />
                <h2 className="text-xl font-semibold">Support Settings</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Support Email</label>
                  <input
                    type="email"
                    value={supportSettings.support_email}
                    onChange={(e) => setSupportSettings({...supportSettings, support_email: e.target.value})}
                    placeholder="support@yourdomain.com"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Support messages will be sent to this email</p>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="auto-reply-enabled"
                    checked={supportSettings.auto_reply_enabled}
                    onChange={(e) => setSupportSettings({...supportSettings, auto_reply_enabled: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <label htmlFor="auto-reply-enabled" className="text-sm">Send auto-reply to users</label>
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Auto-reply Message</label>
                  <textarea
                    value={supportSettings.auto_reply_message}
                    onChange={(e) => setSupportSettings({...supportSettings, auto_reply_message: e.target.value})}
                    placeholder="Thank you for contacting us..."
                    rows={3}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white resize-none"
                  />
                </div>
                <button
                  onClick={async () => {
                    try {
                      await api.post('/admin/support-settings', supportSettings);
                      toast.success('Support settings saved!');
                    } catch (e) { toast.error('Failed to save'); }
                  }}
                  className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-full"
                >
                  Save Support Settings
                </button>
              </div>
            </div>

            {/* Support Messages */}
            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Support Messages</h2>
                <span className="text-sm text-muted-foreground">{supportMessages.length} messages</span>
              </div>
              {supportMessages.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No support messages yet</p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {supportMessages.map((msg) => (
                    <div key={msg.id} className={`p-4 rounded-xl border ${
                      msg.status === 'open' ? 'bg-yellow-500/10 border-yellow-500/30' :
                      msg.status === 'resolved' ? 'bg-green-500/10 border-green-500/30' :
                      'bg-white/5 border-white/10'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{msg.subject}</span>
                        <select
                          value={msg.status}
                          onChange={async (e) => {
                            try {
                              await api.put(`/admin/support-messages/${msg.id}/status`, null, { params: { status: e.target.value } });
                              fetchData();
                            } catch (err) { toast.error('Failed to update status'); }
                          }}
                          className="text-xs px-2 py-1 bg-white/10 border border-white/20 rounded-lg text-white"
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{msg.message.substring(0, 200)}{msg.message.length > 200 ? '...' : ''}</p>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{msg.user_name} ({msg.user_email})</span>
                        <span>{new Date(msg.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Future: Chatbot Settings */}
            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl opacity-50">
              <div className="flex items-center space-x-2 mb-4">
                <MessageSquare className="w-6 h-6 text-purple-400" />
                <h2 className="text-xl font-semibold">AI Chatbot</h2>
                <span className="text-xs bg-purple-500/30 text-purple-300 px-2 py-0.5 rounded-full">Coming Soon</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Configure an AI-powered chatbot to automatically respond to common customer questions.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
